import { type JSX, useCallback, useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { ComplianceRequirementsApi } from 'Api/ComplianceRequirements/ComplianceRequirementsApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { AssociatedControlsModal, AssociatedControlsModalProps } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { COMPLIANCE_REQUIREMENT_DETAILS } from 'Config/PathTemplates';
import { ComplianceRequirement, ComplianceRequirementsForRegulationResponse } from 'Models/ComplianceRequirements';
import { OperationalControl } from 'Models/OperationalControls';
import { OptionType } from 'Models/Types/GlobalType';

import { RequirementListing, RequirementListingProps } from './Components/RequirementListing';

// Determines which, if any, modal should be rendered on the page.
export enum Modal {
    MappedControls,
    None,
}

// The Regulation to load by default when the page loads.
const DEFAULT_REGULATION = 'SOC 2 (March 2020)';

export interface ComplianceRequirementsProps {
    complianceRequirementsApi: ComplianceRequirementsApi;
    controlsApi: ControlsApi;
}

export const ComplianceRequirements = (props: ComplianceRequirementsProps): JSX.Element => {
    const { regulations: availableRegulations } = useCachedData();
    const location = useLocation();
    const navigate = useNavigate();

    // If the user is linked to this page for a specific regulation, preselect it.
    const [selectedRegulation, setSelectedRegulation] = useState<string | undefined>(() => {
        if (location.hash && location.hash.length > 0) {
            return decodeURI(location.hash.substring(1));
        }
        return undefined;
    });

    // Renders a specified modal, or no modal.
    const [displayedModal, setDisplayedModal] = useState<Modal>(Modal.None);
    const [error, setError] = useState<string>();
    const [isGettingComplianceRequirements, setIsGettingComplianceRequirements] = useState(false);
    const [complianceRequirements, setComplianceRequirements] = useState<ComplianceRequirementsForRegulationResponse>();
    // Allows the behavior of the page to change based on "selecting" a Compliance Requirement. At the moment, this is only used by the Mapped Controls modals, but it could be used for/shared by future functionality.
    const [selectedComplianceRequirement, setSelectedComplianceRequirement] = useState<ComplianceRequirement>();

    const getComplianceRequirements = useCallback(
        (selectedRegulation: string) => {
            const getRequirement = async () => {
                try {
                    setIsGettingComplianceRequirements(true);
                    const getComplianceRequirementsResponse = await props.complianceRequirementsApi.getComplianceRequirementsForRegulation(selectedRegulation);
                    const complianceRequirements = getComplianceRequirementsResponse.data;
                    setComplianceRequirements(complianceRequirements);
                } catch (error) {
                    setError(error.message);
                } finally {
                    setIsGettingComplianceRequirements(false);
                }
            };
            getRequirement();
        },
        [props.complianceRequirementsApi]
    );

    useEffect(() => {
        if (selectedRegulation) {
            const encodedRegulation = encodeURIComponent(selectedRegulation);
            navigate(`${location.pathname}#${encodedRegulation}`, { replace: true });
            getComplianceRequirements(selectedRegulation);
        }
    }, [selectedRegulation, getComplianceRequirements, location.pathname, navigate]);

    // Set the initial Regulation once cached Regulations are available.
    useEffect(() => {
        if (selectedRegulation !== undefined || location.hash || availableRegulations.length === 0) {
            return;
        }

        // Load a specific Regulation when the page loads, else load the first Regulation alphabetically.
        if (availableRegulations.some((regulation) => regulation.name === DEFAULT_REGULATION)) {
            setSelectedRegulation(DEFAULT_REGULATION);
        } else {
            setSelectedRegulation(availableRegulations[0].name);
        }
    }, [availableRegulations, location.hash, selectedRegulation]);

    /**
     * Display the modal that shows the Controls a Compliance Requirement is mapped to.
     */
    const displayMappedControlsModal = (selectedComplianceRequirement: ComplianceRequirement): void => {
        setDisplayedModal(Modal.MappedControls);
        setSelectedComplianceRequirement(selectedComplianceRequirement);
    };

    /**
     * Initialize properties of the AssociatedControlsModal (Mapped Controls modal).
     */
    const associatedControlsModalProps = (): AssociatedControlsModalProps => {
        // Flatten the Compliance Requirement's assigned_control_sets attribute.
        const associatedControls: OperationalControl[] = [];
        if (selectedComplianceRequirement) {
            for (const control_list of selectedComplianceRequirement.assigned_control_sets) {
                for (const control of control_list) {
                    associatedControls.push(control);
                }
            }
        }

        if (!selectedComplianceRequirement) {
            return {
                hideModal: () => {
                    setDisplayedModal(Modal.None);
                    setSelectedComplianceRequirement(undefined);
                },
                associatedControls: associatedControls,
            };
        }

        return {
            hideModal: () => {
                setDisplayedModal(Modal.None);
                setSelectedComplianceRequirement(undefined);
            },
            associatedControls: associatedControls,
            mapControlsTo: generatePath(COMPLIANCE_REQUIREMENT_DETAILS, {
                regulationName: selectedComplianceRequirement.regulation,
                requirement: selectedComplianceRequirement.identifier,
            }),
        };
    };

    const createRegulationOptions = (): OptionType[] => {
        return availableRegulations.map((regulation) => {
            return {
                label: regulation.name,
                value: regulation.name,
            };
        });
    };

    const handleSelectChange = (value: ChangeEventType): void => {
        setSelectedRegulation(value as string);
        getComplianceRequirements(value as string);
    };

    const requirementListingProps: RequirementListingProps = {
        displayMappedControlsModal: displayMappedControlsModal,
        regulationName: selectedRegulation!,
        complianceRequirements: complianceRequirements,
    };

    if (complianceRequirements) {
        return (
            <>
                {displayedModal === Modal.MappedControls && selectedComplianceRequirement && <AssociatedControlsModal {...associatedControlsModalProps()} />}
                <PageLayoutDefault
                    headerTitle="Compliance Requirements"
                    headerButtons={<FormFieldSelect options={createRegulationOptions()} formFieldId="selectedRegulation" formFieldLabel="Regulation" handleChange={handleSelectChange} selectedOption={selectedRegulation} disabled={isGettingComplianceRequirements} notClearable />}
                    body={[
                        {
                            title: selectedRegulation,
                            content: isGettingComplianceRequirements ? <Placeholder /> : <RequirementListing {...requirementListingProps} />,
                        },
                    ]}
                />
            </>
        );
    } else if (error) {
        return <Text>{error}</Text>;
    } else return <Placeholder />;
};
